import {
  type EcrituresRewrite,
  formatAccountLabel,
  type SetTiersAccountInput,
  type TiersAccountState,
} from '@cpce/shared';
import { useState } from 'react';
import {
  useClientAccountNextCode,
  useConnectableClientAccounts,
  useConnectableSupplierAccounts,
  useSupplierAccountNextCode,
} from '@/api/financial-accounts/financial-accounts.queries';
import { Combobox } from '@/components/Combobox';
import { Input } from '@/components/Input';
import { KeptEcrituresIndicator } from '@/components/KeptEcrituresIndicator';
import { TiersAccountChangeModal } from '@/components/TiersAccountChangeModal';

const TIERS_ACCOUNT = {
  salarié: {
    prefix: '411',
    modalTitle: 'Changer le compte client',
    useConnectable: useConnectableClientAccounts,
    useNextCode: useClientAccountNextCode,
  },
  fournisseur: {
    prefix: '401',
    modalTitle: 'Changer le compte fournisseur',
    useConnectable: useConnectableSupplierAccounts,
    useNextCode: useSupplierAccountNextCode,
  },
};

type Account = { code: string; name: string | null };

interface TiersAccountChoiceOptions {
  tiers: keyof typeof TIERS_ACCOUNT;
  /** The account shown today; null when the tiers has none. */
  account: Account | null;
  state?: TiersAccountState;
  /** Prefills the name of a created account. */
  defaultName: string;
  /** Loads the connectable accounts and the next free code. */
  isEditing: boolean;
  /** Offers « Conserver » first; the choice starts empty without it. */
  canKeep?: boolean;
  /** Offers to leave the tiers without an account. */
  canClear?: boolean;
  save: (input: SetTiersAccountInput | null) => Promise<EcrituresRewrite>;
}

/**
 * Picks a salarié's 411 or a supplier's 401: keep it, take its history back onto it, connect an
 * unlinked one, create one or (a supplier) clear it. A change on a tiers with history asks first
 * whether that history follows.
 */
export function useTiersAccountChoice(options: TiersAccountChoiceOptions) {
  const { tiers, account, state, defaultName, isEditing, canKeep, canClear, save } = options;
  const { prefix, modalTitle, useConnectable, useNextCode } = TIERS_ACCOUNT[tiers];
  const initialChoice = canKeep ? 'keep' : '';
  const [choice, setChoice] = useState(initialChoice);
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);
  const { data: connectableAccounts = [], isLoading } = useConnectable(isEditing);
  const { data: nextCode } = useNextCode(isEditing);
  const entriesOnOtherAccounts = state?.entriesOnOtherAccounts ?? [];

  const reset = () => {
    setChoice(initialChoice);
    setNewCode('');
    setNewName('');
  };

  const inputFor = (moveEntries: boolean): SetTiersAccountInput | null => {
    if (choice === 'clear') return { mode: 'clear', moveEntries: false };
    if (choice === 'create') return { mode: 'create', code: newCode, name: newName, moveEntries };
    if (choice.startsWith('connect:')) {
      const financialAccountId = Number(choice.slice('connect:'.length));
      return { mode: 'connect', financialAccountId, moveEntries };
    }
    return null;
  };

  const requestSave = async () => {
    const input = inputFor(false);
    const hasHistory = account !== null || entriesOnOtherAccounts.length > 0;
    if (input && input.mode !== 'clear' && hasHistory) {
      setIsConfirming(true);
      return;
    }
    await save(input);
  };

  const editor = (
    <div className="flex flex-col gap-2">
      <Combobox
        options={[
          ...(canKeep
            ? [{ value: 'keep', label: account ? `Conserver (${account.code})` : 'Aucun compte' }]
            : []),
          ...(state?.account && entriesOnOtherAccounts.length > 0
            ? [
                {
                  value: `connect:${state.account.id}`,
                  label: `Reprendre l'historique sur ${state.account.code}`,
                },
              ]
            : []),
          ...(canClear && account ? [{ value: 'clear', label: 'Aucun (détacher le compte)' }] : []),
          ...connectableAccounts.map((connectable) => ({
            value: `connect:${connectable.id}`,
            label: formatAccountLabel(connectable),
          })),
          { value: 'create', label: '＋ Créer un nouveau compte' },
        ]}
        value={choice || undefined}
        onChange={(value) => {
          setChoice(value);
          if (value === 'create') {
            setNewCode((prev) => prev || (nextCode?.code ?? ''));
            setNewName((prev) => prev || defaultName);
          }
        }}
        isLoading={isLoading}
      />
      {choice === 'create' && (
        <>
          <Input
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            placeholder={`Numéro de compte (${prefix}…)`}
          />
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nom du compte"
          />
        </>
      )}
    </div>
  );

  const view = (
    <div className="flex items-center gap-2">
      <span>
        {account
          ? `${account.code}${account.name ? ` — ${account.name}` : ''}`
          : 'Aucun — à connecter'}
      </span>
      <KeptEcrituresIndicator
        entries={entriesOnOtherAccounts}
        title={`Écritures du ${tiers} restées sur un autre compte`}
      />
    </div>
  );

  const modal = isConfirming && (
    <TiersAccountChangeModal
      title={modalTitle}
      tiers={tiers}
      onConfirm={(moveEntries) => save(inputFor(moveEntries))}
      closeModal={() => setIsConfirming(false)}
    />
  );

  return { editor, view, modal, reset, requestSave, hasChoice: choice !== initialChoice };
}
